import { useMemo } from 'react';
import { useDateFormat } from '@eti/providers';
import { Inline, vars } from '@etg/wings';
import { TripType } from '@eti/schema-types';
import { useAuth } from '@eti/authentication';
import { css } from '@eti/styles';
import { date as dateUtil } from '@eti/utils';
import BidirectionalSeparatedElements from '../../../../common/bidirectional-component/BidirectionalSeparatedElements';
import { useOrderDetails } from '../../containers/OrderDetailsContext';

const latestDepartureDateStyles = (isDateChanged: boolean) => css`
  background-color: ${isDateChanged ? vars.colors.signal.caution.tint : 'transparent'};
`;

const originalDepartureDateStyles = css`
  text-decoration: line-through;
`;

interface DatesProps {
  shouldShowOriginal?: boolean;
}

const Dates = ({ shouldShowOriginal = false }: DatesProps) => {
  const { user } = useAuth();
  const { day } = useDateFormat();
  const { bounds, journeyType } = useOrderDetails();
  const { latestBoundDepartureDates, originalBoundDepartureDates, isDepartureDateChanged } =
    useMemo(() => {
      const formatDate = (value: string) => (value ? dateUtil.format(value, day) : '');
      let hasDateChanges = false;

      const { latestDepartureDates, originalDepartureDates } = bounds.reduce(
        (acc: { latestDepartureDates: string[]; originalDepartureDates: string[] }, bound) => {
          if (bound.segments[0].__typename === 'TripSegment') {
            const latestDepartureDate = formatDate(bound.segments[0].departuredAt);
            acc.latestDepartureDates.push(latestDepartureDate);

            if (shouldShowOriginal) {
              const originalDepartureDate = bound.originalVersion
                ? formatDate(bound.originalVersion.originalSegments[0].departuredAt)
                : latestDepartureDate;

              hasDateChanges = hasDateChanges || latestDepartureDate !== originalDepartureDate;
              acc.originalDepartureDates.push(originalDepartureDate);
            }
          }
          return acc;
        },
        { latestDepartureDates: [], originalDepartureDates: [] },
      );

      return {
        latestBoundDepartureDates: latestDepartureDates,
        originalBoundDepartureDates: originalDepartureDates,
        isDepartureDateChanged: hasDateChanges,
      };
    }, [bounds, day, shouldShowOriginal]);

  return (
    <Inline alignY="center" data-testid="trip-dates" spacing={8}>
      {isDepartureDateChanged && (
        <BidirectionalSeparatedElements
          className={originalDepartureDateStyles}
          data-testid={`order-${user?.sessionOrderNumber}-bound-original-departure-dates`}
          elements={originalBoundDepartureDates}
          separator={journeyType === TripType.MultiStop ? ', ' : ' - '}
        />
      )}
      <BidirectionalSeparatedElements
        className={latestDepartureDateStyles(isDepartureDateChanged)}
        data-testid={`order-${user?.sessionOrderNumber}-bound-departure-dates`}
        elements={latestBoundDepartureDates}
        separator={journeyType === TripType.MultiStop ? ', ' : ' - '}
      />
    </Inline>
  );
};

export default Dates;
